/**
 * The roster. Server-owned, shared with the client for VFX and telegraphs.
 *
 * THE CLIENT NEVER SUPPLIES A NUMBER FROM THIS FILE. It reads these values to
 * draw a cooldown ring, size a telegraph decal, and pick a color. Every value
 * that decides an outcome is read by the server from this same table, and the
 * server does not trust a client's copy of it.
 *
 * Tuning lives here and only here, so a balance pass is one file and one
 * deploy.
 *
 * ── TICKERS ARE DISPLAY NAMES. IDS ARE NOT. ───────────────────────────────
 *
 * The roster reads NVDA / AAPL / GOOGL / TSLA / SPCX on screen, and `volt` /
 * `monolith` / `hex` / `mirage` / `talon` in every identifier below. That is
 * deliberate and it is not laziness:
 *
 *   - `characterId` is a String column on the match table and a field in every
 *     replay header. Renaming the VALUES would orphan every stored row and
 *     every archived replay — evidence a dispute is settled from. A theme is
 *     not worth invalidating the audit trail.
 *   - The archetypes outlive the theme. `volt` is the assassin whatever ticker
 *     is painted on it, and STOCK-THEME §2 says exactly that: the ticker
 *     inherits the archetype, once, by hand.
 *
 * The same split runs one level down: ability IDS keep their archetype names,
 * so the code and its comments still say `fault_line` where the UI says
 * "Supercycle". The bridge, once, here — grep either column and land in the
 * right place:
 *
 *   NVDA   blink_strike  Parallel Path  | overload      Overclock      | chain_lightning CUDA Cascade
 *   AAPL   seismic_slam  One More Thing | bulwark       Walled Garden  | fault_line      Supercycle
 *   GOOGL  glyph_trap    Tracking Pixel | gravity_well  PageRank       | null_zone       Deindexed
 *   TSLA   phantom       Autopilot      | fade          Ludicrous Mode | displace        Robotaxi
 *   SPCX   arc_shot      Falcon         | perch         Hover Slam     | barrage         Starlink
 *
 * The THEME pass touched `name`, `fantasy` and `description` and nothing else
 * — STOCK-THEME §2 calls that out as the property that made the re-skin free.
 * The ROLE pass that followed (see the note above the basics) deliberately
 * moved health, speed and every basic attack, and `characters.balance.test.ts`
 * was rewritten in the same commit; that is the paper trail a balance change
 * is supposed to leave. Skills and ultimates are still the theme pass's numbers.
 *
 * `colorHex` IS THE CHARACTER'S IDENTITY, and there is exactly one of it.
 *
 * It was a separate abstract palette — cyan / orange / violet / mint / amber —
 * chosen for maximum separability back when nothing else carried a character's
 * colour. Then the procedural bodies arrived with their own family palettes and
 * the game started disagreeing with itself: a player's body was deep green
 * while their nameplate, telegraph and lobby card were cyan. Two sources of
 * truth for "what colour is this character", which is one too many.
 *
 * These derive from the TICKER each character is, because that is what a
 * player recognises: green for NVDA, graphite for AAPL, blue for GOOGL, red for
 * TSLA, exhaust orange for SPCX. The lobby, the cards, the nameplates and the
 * models all take this one value.
 *
 * BRAND FAMILY, NOT BRAND HEX. This file used to avoid corporate palettes
 * entirely, on the grounds that a company's exact brand colour alongside its
 * name reads more like a claim of affiliation than a ticker does on its own.
 * That reasoning still stands and the values below respect it: they are shifted
 * in hue and saturation far enough that no eyedropper matches Google Blue or
 * Tesla Red, and close enough that nobody has to be told which ticker they are.
 * If a future change reaches for an exact brand value, this is the paragraph it
 * has to argue with first.
 *
 * WHAT THIS VALUE IS NOT: a colour it is safe to paint on the arena floor. See
 * `telegraphHex` below — that is a different question with a different answer,
 * and collapsing the two is how you get a maroon danger zone drawn next to a
 * red hazard pool.
 */
export const ROLE_LABELS = {
    tank: "Tank · melee",
    assassin: "Assassin · melee",
    gunner: "Gunner · mid range",
    controller: "Controller · mid range",
    marksman: "Marksman · long range",
};
/** Three pips, like the reference. Per-character variety lives in `reloadMs`. */
export const AMMO_MAX = 3;
/**
 * The fire cooldown every basic shares, in ms. Short enough that the pips are
 * the limit, not the trigger; long enough that a held button cannot dump three
 * pips inside one wind-up.
 */
export const BASIC_FIRE_COOLDOWN_MS = 200;
/**
 * Angular offset of pellet `index` from the aim, in radians.
 *
 * Spread: pellets fanned evenly from -spread/2 to +spread/2. Burst: a fixed
 * wobble pattern (+, -, +½, -½) of half the spread, so a four-shot burst reads
 * as a hail rather than a ruler line. Pure arithmetic on the table's numbers,
 * so the sim and the shooter's own client draw the same fan.
 */
export function pelletOffsetRad(shape, index) {
    const spread = (shape.spreadDeg * Math.PI) / 180;
    if (shape.pellets <= 1 || spread <= 0)
        return 0;
    if (isBurst(shape)) {
        const wobble = BURST_WOBBLE[index % BURST_WOBBLE.length] ?? 0;
        return (wobble * spread) / 2;
    }
    return spread * (index / (shape.pellets - 1) - 0.5);
}
const BURST_WOBBLE = [1, -1, 0.5, -0.5];
/**
 * A burst: one press, several rounds fired one `burstIntervalMs` apart down the
 * same aim. Anything else with several pellets is a spread, fired as one volley.
 */
export function isBurst(shape) {
    return shape.pellets > 1 && shape.burstIntervalMs > 0;
}
/**
 * How far the basic attack threatens, in units, from the caster's centre.
 *
 * A melee cone reaches its range plus any lunge; a shot reaches its range. The
 * aim indicator, the bots and the balance model all read this one function,
 * which is what keeps "the line on the floor", "when a bot presses fire" and
 * "who out-ranges whom" the same number.
 */
export function basicReach(ability) {
    const hit = ability.hit;
    switch (hit.kind) {
        case "melee_cone":
            return hit.range + (ability.selfMove ?? 0);
        case "shot":
            return hit.maxRange;
        case "projectile":
            return hit.maxRange;
        case "dash_line":
            return ability.selfMove && ability.selfMove > 0 ? ability.selfMove : hit.length;
        case "sphere":
            return hit.radius;
        case "ground_zone":
            return hit.radius;
        case "self":
            return 0;
    }
}
/** Damage one cast of the basic deals if everything connects, after the character's multiplier. */
export function basicDamagePerCast(def) {
    const basic = def.abilities.basic;
    const pellets = basic.hit.kind === "shot" ? basic.hit.pellets : 1;
    return basic.damage * pellets * def.damageMult;
}
/**
 * The shortest interval between two casts of the basic, in ms.
 *
 * The sim refuses a cast while `busy` (wind-up plus recovery) and while the
 * fire cooldown runs; whichever is longer is the cadence.
 */
export function basicCadenceMs(def) {
    const basic = def.abilities.basic;
    return Math.max(basic.cooldownMs, basic.windupMs + basic.recoveryMs);
}
/**
 * Milliseconds for `def` to deal `hp` with its basic alone, from a full
 * magazine, every hit landing.
 *
 * The magazine model is the sim's: pips are spent at the cadence, and once
 * they are gone the next one arrives every `reloadMs` from the moment the
 * first was spent (the reload runs continuously from the first shot). The
 * final hit lands `windupMs` plus any `selfMoveMs` after its cast — a lunge
 * cuts when it ARRIVES, not when it leaves; bullet travel is ignored.
 *
 * This is the number the balance test bands and the PR table quotes. It is a
 * stand-and-deliver figure: it says nothing about reach, speed or dodging,
 * which is what the matchup model in `@chaos/bot` adds.
 */
export function basicTimeToKillMs(def, hp) {
    const basic = def.abilities.basic;
    const perCast = basicDamagePerCast(def);
    if (perCast <= 0)
        return Infinity;
    const casts = Math.ceil(hp / perCast);
    const cadence = basicCadenceMs(def);
    const max = basic.ammo?.max ?? 1;
    const reload = basic.ammo?.reloadMs ?? 0;
    let t = 0;
    for (let k = 0; k < casts; k++) {
        // Pip k is in the magazine for k < max; after that it is the (k - max + 1)th
        // pip to come back, `reload` apart, counting from the first cast.
        const available = k < max ? 0 : (k - max + 1) * reload;
        t = k === 0 ? 0 : Math.max(t + cadence, available);
    }
    return t + basic.windupMs + (basic.selfMoveMs ?? 0);
}
/** Sustained damage per second of the basic once the magazine is spent: one cast per reload. */
export function basicSustainedDps(def) {
    const basic = def.abilities.basic;
    const reload = basic.ammo?.reloadMs ?? basicCadenceMs(def);
    return (basicDamagePerCast(def) * 1000) / Math.max(reload, basicCadenceMs(def));
}
/**
 * The one place the body/world split is decided. The sim's hit resolution and
 * the client's telegraph/VFX both consult this — two independent copies of
 * this rule is exactly the drift it exists to prevent.
 *
 * Derived from the hit shape rather than authored per-ability, so a new
 * ability cannot forget to declare it: ground zones and projectiles are places
 * by construction, and a dash_line that does not move its caster (Fault Line)
 * is a crack drawn on the floor. Everything else — melee cones, spheres,
 * self-casts, and dashes the caster rides (Blink Strike) — is the body.
 */
export function abilityAnchor(ability) {
    const kind = ability.hit.kind;
    if (kind === "ground_zone" || kind === "projectile")
        return "world";
    if (kind === "dash_line" && !(ability.selfMove && ability.selfMove > 0))
        return "world";
    // A `shot` leaves the gun where the shooter IS when it fires — the muzzle
    // rides the body through the (short) wind-up — while the aim stays frozen
    // like everything else. Falcon differs: its shell is a place on the map.
    return "body";
}
/**
 * THE BASICS ARE WHERE THE ROLES LIVE.
 *
 * Every character used to share `BASIC()`: "Strike", a 2.6m melee cone on a
 * 550ms cooldown. Five kits, one way to actually hit someone, so every fight
 * happened face to face whatever the skills said — and the roster felt like
 * one character with five hats. The reference game gets its class identity
 * from the basic attack: the tank punches, the assassin dives, the gunners
 * shoot, the sniper shoots further. So now each basic is authored by hand,
 * with an `ammo` magazine (three pips, per-character reload) and the shared
 * `BASIC_FIRE_COOLDOWN_MS`.
 *
 * Two melee, three ranged, and the bodies are widened to match: the tank has
 * three times the assassin's health and moves at two thirds of its speed. The
 * numbers are a counter triangle — tank beats assassin, ranged kites tank,
 * assassin dives ranged — and `characters.balance.test.ts` plus the matchup
 * model in `@chaos/bot` keep every pairing inside a band, so no pick is a
 * coin you cannot win with. Hand-tuned, every one of them; nothing here reads
 * a market, and nothing may.
 */
const basic = (over) => ({
    slot: "basic",
    id: "basic",
    cooldownMs: BASIC_FIRE_COOLDOWN_MS,
    ...over,
});
/**
 * The colour a character's ability may be drawn on the ground in.
 *
 * Derived from `colorHex`, and deliberately not equal to it. The arena deck
 * already spends two hues on rules every player must read instantly: red
 * (#ff3d3d) is a hazard pool that burns you and green (#4ade80) is a mend pad
 * that heals you. Those are fixed across every arena precisely so they never
 * have to be relearned.
 *
 * A telegraph tinted TSLA-red lands next to a hazard lane; one tinted
 * NVDA-green lands next to a mend pad. Under pressure, at phone size, on a
 * floor you are already reading for threat, that is a misread with damage
 * attached.
 *
 * So identity and floor-safety are separated. Identity wants the body colour;
 * the floor wants distance from the signal hues — while keeping enough of the
 * source hue that a player still recognises whose ability it is.
 *
 * The deck itself is NOT what these clear. `ZoneRenderer` draws them with
 * `THREE.AdditiveBlending` — "brightness IS emissive intensity", in its own
 * words — so a telegraph does not need to differ in hue from the floor under
 * it; it brightens that floor, which is how it reads at all. What additive
 * blending cannot rescue is a telegraph mistaken for a hazard pool, because
 * that is a rule misread rather than camouflage. So the signals are the bar,
 * and `palette.test.ts` is where the distance is measured.
 *
 * Kept beside the roster rather than in the renderer because it is a fact
 * about the CHARACTER, and the client is not the only thing that may want it.
 */
const TELEGRAPH_HEX = {
    volt: 0x5ce1ff, // green identity, cyan on the floor
    monolith: 0xeef2be, // graphite has no hue of its own. Warm CREAM, not the
    // violet it was (purple is v1's brand and never v2's) and not amber (amber
    // sat 0.1 degrees from talon's, collapsing two of the five floor colours
    // into one). Cream is the one lane `palette.test.ts` leaves open: hue 65,
    // clear of hazard red and mend green, of talon's amber by 29 degrees, and
    // of the cyan/blue pair that a steel or ice blue would have landed between.
    hex: 0x8fb8ff, // Google blue, lifted off the deck's own blues
    mirage: 0xff8fd8, // magenta. Tesla red cannot go on the floor — that is the
    // hazard signal. The first attempt at this was a pink 9 degrees from hazard
    // red, which kept the family and did NOT clear the signal; `palette.test.ts`
    // now measures the distance instead of asserting it in a comment.
    talon: 0xffc46b, // exhaust amber, brightened; clear of the mend green
};
export function telegraphHex(id) {
    return TELEGRAPH_HEX[id];
}
export const CHARACTERS = {
    volt: {
        id: "volt",
        name: "NVDA",
        archetype: "assassin",
        role: "assassin",
        maxHealth: 80,
        speedMult: 1.3,
        damageMult: 1.15,
        colorHex: 0x2f8f3d,
        fantasy: "Highest beta on the board. Deletes the wounded, or gets deleted trying.",
        playstyle: "Fastest and most fragile. Tensor Strike rolls 3m and cuts on arrival, so dive the shooters — they cannot back off faster than you close. Never trade with AAPL; three pips will not dent it.",
        abilities: {
            basic: basic({
                name: "Tensor Strike",
                damage: 26,
                // No glint: the ROLL is the anticipation. 100 ms of the body visibly
                // coming at you before the cut lands, where it used to be 70 ms of a
                // spark on a body that then arrived inside one tick — more to react to,
                // not less. The cut lands 100 ms after the press (was 70): a slower
                // basic, and the matchup model prices it — the assassin's edge on the
                // marksman thins from 0.56 to 0.55, still favoured. With the old 70 ms
                // glint kept in front of the roll it fell under, and a spark on top
                // of a roll is a tell nobody needs twice.
                windupMs: 0,
                recoveryMs: 130,
                hit: { kind: "melee_cone", range: 2.0, halfAngleDeg: 45 },
                selfMove: 3,
                // A roll, not a blink: three ticks of travel, stopping at the first
                // body, and the cut lands where the roll ends.
                selfMoveMs: 100,
                ammo: { max: AMMO_MAX, reloadMs: 850 },
                description: "Roll 3m and cut on arrival. Three quick pips; the reload is the moment to already be gone.",
            }),
            skill1: {
                slot: "skill1",
                id: "blink_strike",
                name: "Parallel Path",
                damage: 28,
                cooldownMs: 8000,
                windupMs: 120,
                recoveryMs: 180,
                hit: { kind: "dash_line", length: 9, width: 1.6 },
                selfMove: 9,
                // A dash the whole 9 m is travelled in, cutting everything the body
                // passes through on the way — not a blink to the far end.
                selfMoveMs: 200,
                effects: [{ kind: "refund_cooldown_on_kill", pct: 50 }],
                description: "Dash 9m down the line, cutting everything you pass through. Refunds half the cooldown on a kill.",
            },
            skill2: {
                slot: "skill2",
                id: "overload",
                name: "Overclock",
                damage: 0,
                cooldownMs: 12000,
                windupMs: 0,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [{ kind: "speed", pct: 40, durationMs: 2000 }],
                description: "2s of +40% move speed and slow immunity.",
            },
            ultimate: {
                slot: "ultimate",
                id: "chain_lightning",
                name: "CUDA Cascade",
                damage: 45,
                cooldownMs: 0,
                windupMs: 350,
                recoveryMs: 300,
                hit: { kind: "sphere", radius: 11 },
                effects: [{ kind: "chain", maxTargets: 3, falloffPct: 60, radius: 8 }],
                description: "Strikes the nearest target, arcing to two more at 60% falloff.",
            },
        },
    },
    monolith: {
        id: "monolith",
        name: "AAPL",
        archetype: "juggernaut",
        role: "tank",
        maxHealth: 180,
        speedMult: 0.85,
        damageMult: 1.0,
        colorHex: 0xc8ccd4,
        fantasy: "Slow, enormous, and extremely hard to remove. Walks in and stays.",
        playstyle: "Twice anyone's health and a Unibody swing that shoves. Whoever dives you dies; whoever has a gun will not let you reach them, so close through walls and behind Walled Garden.",
        abilities: {
            basic: basic({
                name: "Unibody",
                damage: 20,
                windupMs: 140,
                recoveryMs: 180,
                hit: { kind: "melee_cone", range: 2.8, halfAngleDeg: 60 },
                knockback: 1.2,
                ammo: { max: AMMO_MAX, reloadMs: 1000 },
                description: "Heavy two-handed swing. Slow to start, shoves whoever it lands on.",
            }),
            skill1: {
                slot: "skill1",
                id: "seismic_slam",
                name: "One More Thing",
                damage: 30,
                cooldownMs: 9000,
                windupMs: 400,
                recoveryMs: 320,
                hit: { kind: "sphere", radius: 6 },
                knockback: 4,
                description: "Ground slam. Damages and knocks back everything within 6m.",
            },
            skill2: {
                slot: "skill2",
                id: "bulwark",
                name: "Walled Garden",
                damage: 0,
                cooldownMs: 14000,
                windupMs: 150,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [
                    { kind: "damage_reduction", pct: 50, durationMs: 3000 },
                    { kind: "no_dash", durationMs: 3000 },
                ],
                description: "3s of 50% damage reduction. Cannot dash while active.",
            },
            ultimate: {
                slot: "ultimate",
                id: "fault_line",
                name: "Supercycle",
                damage: 55,
                cooldownMs: 0,
                windupMs: 600,
                recoveryMs: 400,
                hit: { kind: "dash_line", length: 14, width: 3 },
                effects: [{ kind: "stun", durationMs: 2000 }],
                description: "Cracks the ground in a line. Heavy damage and a 2s stun.",
            },
        },
    },
    hex: {
        id: "hex",
        name: "GOOGL",
        archetype: "controller",
        role: "controller",
        maxHealth: 110,
        speedMult: 0.95,
        damageMult: 0.9,
        colorHex: 0x3f7ee0,
        fantasy: "Never chases you. Owns the ground you were going to stand on.",
        playstyle: "Crawler is slow, wide and goes through everyone in a line — fire it down the lane they must use, not at them. Tracking Pixel taxes the other route, PageRank drags them onto both.",
        abilities: {
            basic: basic({
                name: "Crawler",
                damage: 30,
                windupMs: 120,
                recoveryMs: 160,
                hit: {
                    kind: "shot",
                    pellets: 1,
                    spreadDeg: 0,
                    burstIntervalMs: 0,
                    speed: 18,
                    radius: 1.0,
                    maxRange: 11,
                    pierce: true,
                },
                knockback: 0.6,
                ammo: { max: AMMO_MAX, reloadMs: 1200 },
                description: "A slow, wide orb that pierces every body in its path. Aim at the lane, not the player.",
            }),
            skill1: {
                slot: "skill1",
                id: "glyph_trap",
                name: "Tracking Pixel",
                damage: 22,
                cooldownMs: 7000,
                windupMs: 250,
                recoveryMs: 180,
                hit: { kind: "ground_zone", radius: 3, durationMs: 12000 },
                effects: [{ kind: "slow", pct: 50, durationMs: 2000 }],
                description: "Arms a rune. Damages and slows whoever triggers it.",
            },
            skill2: {
                slot: "skill2",
                id: "gravity_well",
                name: "PageRank",
                damage: 0,
                cooldownMs: 13000,
                windupMs: 400,
                recoveryMs: 250,
                hit: { kind: "sphere", radius: 8 },
                effects: [{ kind: "pull", strength: 6 }],
                description: "Drags everyone within 8m toward a point over one second.",
            },
            ultimate: {
                slot: "ultimate",
                id: "null_zone",
                name: "Deindexed",
                damage: 0,
                cooldownMs: 0,
                windupMs: 500,
                recoveryMs: 300,
                hit: { kind: "ground_zone", radius: 10, durationMs: 5000 },
                effects: [
                    { kind: "suppress_abilities", durationMs: 5000 },
                    { kind: "no_dash", durationMs: 5000 },
                    { kind: "damage_over_time", dps: 8, durationMs: 5000 },
                ],
                description: "A dome where nobody may ult or dash, ticking damage.",
            },
        },
    },
    mirage: {
        id: "mirage",
        name: "TSLA",
        archetype: "trickster",
        role: "gunner",
        maxHealth: 90,
        speedMult: 1.1,
        damageMult: 1.0,
        colorHex: 0xb02832,
        fantasy: "You are never certain which one is real. The 1v3 you should have lost.",
        playstyle: "Your three skills deal no damage — Sentry Burst is your only source, 12m of it. Shoot, then not be there: Autopilot leaves a copy running on, Ludicrous Mode covers the exit, Robotaxi rides you out.",
        abilities: {
            basic: basic({
                name: "Sentry Burst",
                damage: 6.5,
                windupMs: 60,
                recoveryMs: 240,
                hit: {
                    kind: "shot",
                    pellets: 4,
                    spreadDeg: 5,
                    burstIntervalMs: 70,
                    speed: 34,
                    radius: 0.2,
                    maxRange: 12,
                    pierce: false,
                },
                ammo: { max: AMMO_MAX, reloadMs: 1150 },
                description: "Four fast rounds, 70ms apart, with a little wobble. Each one hits the first body it meets.",
            }),
            skill1: {
                slot: "skill1",
                id: "phantom",
                name: "Autopilot",
                damage: 0,
                cooldownMs: 10000,
                windupMs: 120,
                recoveryMs: 100,
                hit: { kind: "self" },
                effects: [{ kind: "decoy", durationMs: 4000 }],
                description: "Spawns a decoy on you that keeps running your way. Deals no damage.",
            },
            skill2: {
                slot: "skill2",
                id: "fade",
                name: "Ludicrous Mode",
                damage: 0,
                cooldownMs: 11000,
                windupMs: 0,
                recoveryMs: 0,
                hit: { kind: "self" },
                effects: [
                    { kind: "invisible", durationMs: 1500 },
                    { kind: "speed", pct: 25, durationMs: 1500 },
                ],
                description: "1.5s of near-invisibility — a faint shimmer remains — plus speed.",
            },
            ultimate: {
                slot: "ultimate",
                id: "displace",
                name: "Robotaxi",
                damage: 0,
                cooldownMs: 0,
                windupMs: 300,
                recoveryMs: 200,
                hit: { kind: "sphere", radius: 20 },
                // The ride: both bodies travel to each other's spot, passing in the
                // middle. At least this long; a ride near the 20 m edge stretches it.
                selfMoveMs: 300,
                effects: [{ kind: "swap_position" }],
                description: "Ride: you and any target within 20m trade places, passing each other on the way.",
            },
        },
    },
    talon: {
        id: "talon",
        name: "SPCX",
        archetype: "artillery",
        role: "marksman",
        maxHealth: 85,
        speedMult: 0.95,
        damageMult: 1.1,
        colorHex: 0xf0b478,
        fantasy: "Pressure from somewhere you would rather it weren't. Nobody is near you.",
        playstyle: "Raptor is the longest gun on the board: 21m, one hard round. Distance is your health bar — NVDA closes it faster than you back off, so keep a wall between you. Falcon flies flat; a wall stops it.",
        abilities: {
            basic: basic({
                name: "Raptor",
                damage: 34,
                windupMs: 130,
                recoveryMs: 170,
                hit: {
                    kind: "shot",
                    pellets: 1,
                    spreadDeg: 0,
                    burstIntervalMs: 0,
                    speed: 46,
                    radius: 0.22,
                    maxRange: 21,
                    pierce: false,
                },
                ammo: { max: AMMO_MAX, reloadMs: 1150 },
                description: "One fast, straight round with the longest reach in the game. Flat: a wall stops it.",
            }),
            skill1: {
                slot: "skill1",
                id: "arc_shot",
                name: "Falcon",
                damage: 26,
                cooldownMs: 5000,
                windupMs: 200,
                recoveryMs: 180,
                // The sim launches at 15 degrees (PROJECTILE_LAUNCH_* in @chaos/sim):
                // flat enough that the shell threatens standing bodies along its whole
                // flight instead of only at a distant landing ring. Against g=19 that
                // carries ~19m on flat ground; height under the caster — a Perch —
                // stretches the arc toward `maxRange`.
                hit: { kind: "projectile", speed: 24, radius: 2.5, maxRange: 30, gravity: 19 },
                description: "Flat-arced shell with a 2.5m splash on impact.",
            },
            skill2: {
                slot: "skill2",
                id: "perch",
                name: "Hover Slam",
                damage: 0,
                cooldownMs: 12000,
                windupMs: 100,
                recoveryMs: 0,
                hit: { kind: "self" },
                selfMove: 8,
                // A jump arc, eight ticks up, into the hover — not an 8 m pop.
                selfMoveMs: 267,
                effects: [{ kind: "hover", durationMs: 2000 }],
                // No hidden range stat: the altitude itself is what stretches Arc
                // Shot's ballistic carry. The tooltip promises only what the sim does.
                description: "Leap 8m up over a quarter second and hover — high ground carries Falcon further. Also a billboard.",
            },
            ultimate: {
                slot: "ultimate",
                id: "barrage",
                name: "Starlink",
                damage: 15,
                cooldownMs: 0,
                windupMs: 500,
                recoveryMs: 400,
                hit: { kind: "ground_zone", radius: 12, durationMs: 3000 },
                effects: [{ kind: "repeat", count: 6, intervalMs: 500 }],
                description: "Six shells over three seconds across a wide area.",
            },
        },
    },
};
export const CHARACTER_IDS = Object.keys(CHARACTERS);
export function isCharacterId(v) {
    return Object.prototype.hasOwnProperty.call(CHARACTERS, v);
}
export function getCharacter(id) {
    const c = CHARACTERS[id];
    if (!c)
        throw new Error(`Unknown character: ${id}`);
    return c;
}
/** MVP roster. M2 opens the rest. */
export const MVP_CHARACTERS = ["volt", "monolith", "talon"];
//# sourceMappingURL=characters.js.map